<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const searchQuery = ref('');
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const filteredWhatsAppCampaigns = computed(() => {
  const list = Array.isArray(WhatsAppCampaigns.value)
    ? WhatsAppCampaigns.value
    : [];
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return list;

  return list.filter(campaign =>
    [campaign?.title, campaign?.message, campaign?.inbox?.name]
      .filter(Boolean)
      .some(value => value.toString().toLowerCase().includes(query))
  );
});

const hasNoWhatsAppCampaigns = computed(
  () =>
    filteredWhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :header-description="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    header-icon="i-ph-whatsapp-logo-bold"
    @click="toggleWhatsAppCampaignDialog()"
    @close="toggleWhatsAppCampaignDialog(false)"
  >
    <template #toolbar>
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-xs">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 text-n-slate-11 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
            :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          />
        </div>
        <div class="text-sm text-n-slate-11 self-center">
          {{ filteredWhatsAppCampaigns.length }}
        </div>
      </div>
    </template>

    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        @close="toggleWhatsAppCampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="filteredWhatsAppCampaigns"
      @delete="handleDelete"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>

<style scoped>
.search-input {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #111827;
}

.search-input:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.12);
  background: #ffffff;
}

.search-input::placeholder {
  color: #9ca3af;
}
</style>
